/**
 * useChart — TanStack Query hook for the user's natal chart.
 *
 * Endpoint: GET /api/v1/me/chart
 * Response: { status: 'pending' } | { status: 'ready', sun, moon, ascendant,
 *            venus, mars, mercury, jupiter, saturn, computedAt }
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export interface ChartPlanet {
  id: string;
  sign: string;
  position: number;
  absolutePosition: number;
  house: number;
  retrograde: boolean;
}

export interface ChartHouse {
  house: number;
  sign: string;
  position: number;
  absolutePosition: number;
}

export interface ChartAngle {
  sign: string;
  position: number;
  absolutePosition: number;
  house?: number;
}

export interface ChartAngles {
  asc: ChartAngle;
  mc: ChartAngle;
  ic: ChartAngle;
  dc: ChartAngle;
  vertex?: ChartAngle;
}

export interface ChartAspect {
  planet1: string;
  planet2: string;
  type: string;
  orb: number;
  degrees: number;
  isMajor: boolean;
  isApplying: boolean;
}

export interface NatalChartReady {
  status: 'ready';
  sun: string;
  moon: string;
  ascendant: string;
  venus: string;
  mars: string;
  mercury: string;
  jupiter: string;
  saturn: string;
  computedAt: string;
  archetype: { code: string; label: string } | null;
  dominant: { planet: string } | null;
  planets: ChartPlanet[] | null;
  houses: ChartHouse[] | null;
  angles: ChartAngles | null;
  aspects: ChartAspect[] | null;
}
export interface NatalChartPending {
  status: 'pending';
}
export type NatalChart = NatalChartReady | NatalChartPending;

export function useChart(): {
  data: NatalChart | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);
  const query = useQuery<NatalChart, Error>({
    queryKey: ['me-chart'],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/me/chart', jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`chart_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as NatalChart;
    },
    enabled: !!jwt,
    staleTime: 60_000,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch };
}
